import type { SonicSiloEngine } from './SonicSiloEngine';
import type { MusicBands } from '../../audio/MusicEngine';

export class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;
  private shakeTimer = 0;
  private shakeIntensity = 0;

  constructor(canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get 2D context for SonicSiloDefense');
    }
    this.ctx = context;
  }

  public triggerShake(duration = 0.2, intensity = 6): void {
    this.shakeTimer = duration;
    this.shakeIntensity = intensity;
  }

  public render(engine: SonicSiloEngine, dt: number, musicBands?: MusicBands): void {
    const ctx = this.ctx;
    const w = engine.width;
    const h = engine.height;

    const kick = musicBands ? musicBands.kick : 0;
    const energy = musicBands ? musicBands.energy : 0;

    ctx.save();

    if (this.shakeTimer > 0) {
      this.shakeTimer -= dt;
      const sx = (Math.random() - 0.5) * 2 * this.shakeIntensity;
      const sy = (Math.random() - 0.5) * 2 * this.shakeIntensity;
      ctx.translate(sx, sy);
    }

    // 1. Background Void
    ctx.fillStyle = '#050714';
    ctx.fillRect(0, 0, w, h);

    // 2. Retro Vector Horizon Grid
    this.drawGrid(engine, kick, energy);

    // 3. Ground & Shielded Cities
    this.drawTerrainAndCities(engine, kick);

    // 4. Silos & Ammo Indicators
    this.drawSilos(engine, kick);

    // 5. Orbital Bombers & Supply Drones
    this.drawBombersAndDrones(engine, energy);

    // 6. Interceptor Rockets & Trails
    this.drawInterceptors(engine);

    // 7. Enemy Warheads & Trails
    this.drawWarheads(engine);

    // 8. Shockwaves (Kinetic Rings + Cascade Multipliers)
    this.drawShockwaves(engine, energy);

    // 9. Particles
    this.drawParticles(engine);

    // 10. Floating Score Texts
    this.drawFloatingTexts(engine);

    // 11. Aiming Reticle & Gravity Pulse Aura
    this.drawReticle(engine);

    // 12. Phase Overlays (START / GAME_OVER)
    if (engine.phase === 'START' || engine.phase === 'GAME_OVER') {
      this.drawOverlay(engine, kick);
    }

    ctx.restore();
  }

  private drawGrid(engine: SonicSiloEngine, kick: number, energy: number): void {
    const ctx = this.ctx;
    const gridAlpha = 0.12 + kick * 0.15 + energy * 0.1;

    ctx.save();
    ctx.strokeStyle = `rgba(0, 255, 200, ${gridAlpha})`;
    ctx.lineWidth = 1;

    const stepX = 80;
    for (let x = 0; x <= engine.width; x += stepX) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      const bottomX = engine.width / 2 + (x - engine.width / 2) * 1.6;
      ctx.lineTo(bottomX, engine.height);
      ctx.stroke();
    }

    const stepY = 60;
    for (let y = 60; y < engine.height; y += stepY) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(engine.width, y);
      ctx.stroke();
    }

    const gradient = ctx.createLinearGradient(0, 500, 0, 600);
    gradient.addColorStop(0, 'rgba(0, 255, 200, 0)');
    gradient.addColorStop(1, `rgba(0, 255, 200, ${0.15 + kick * 0.2})`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 500, engine.width, 100);

    ctx.restore();
  }

  private drawTerrainAndCities(engine: SonicSiloEngine, kick: number): void {
    const ctx = this.ctx;
    ctx.save();

    // Ground line
    ctx.strokeStyle = '#00ffcc';
    ctx.shadowColor = '#00ffcc';
    ctx.shadowBlur = 8;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(0, 560);
    ctx.lineTo(engine.width, 560);
    ctx.stroke();

    // Draw 4 Defendable Neon Cities with Shield Domes & HP Bars
    for (const city of engine.cities) {
      if (!city.active) {
        // Smoldering neon rubble
        ctx.strokeStyle = '#ff2244';
        ctx.fillStyle = '#220810';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(city.x - 18, 560);
        ctx.lineTo(city.x - 10, 550);
        ctx.lineTo(city.x - 2, 556);
        ctx.lineTo(city.x + 7, 548);
        ctx.lineTo(city.x + 18, 560);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        continue;
      }

      // Intact Neon City Spire + Pyramid
      ctx.fillStyle = '#0a2636';
      ctx.strokeStyle = '#00ffcc';
      ctx.lineWidth = 1.5;

      ctx.fillRect(city.x - 14, 535, 10, 25);
      ctx.strokeRect(city.x - 14, 535, 10, 25);
      ctx.fillRect(city.x - 2, 526, 12, 34);
      ctx.strokeRect(city.x - 2, 526, 12, 34);
      ctx.fillRect(city.x + 11, 540, 8, 20);
      ctx.strokeRect(city.x + 11, 540, 8, 20);

      // Protective Shield Dome Arc
      const hpRatio = city.hp / city.maxHp;
      const domeColor = hpRatio > 0.5 ? '#00ffcc' : '#ffaa00';
      ctx.strokeStyle = domeColor;
      ctx.shadowColor = domeColor;
      ctx.shadowBlur = 6 + kick * 6;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(city.x, 560, 26, Math.PI, 0);
      ctx.stroke();

      // HP Bar under city
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#112233';
      ctx.fillRect(city.x - 18, 566, 36, 4);
      ctx.fillStyle = domeColor;
      ctx.fillRect(city.x - 18, 566, 36 * hpRatio, 4);
    }

    ctx.restore();
  }

  private drawSilos(engine: SonicSiloEngine, kick: number): void {
    const ctx = this.ctx;
    ctx.save();

    for (const silo of engine.silos) {
      if (!silo.active) {
        ctx.strokeStyle = '#444455';
        ctx.fillStyle = '#111122';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(silo.x, silo.y, 18, Math.PI, 0);
        ctx.stroke();
        ctx.fill();

        ctx.fillStyle = '#ff3300';
        ctx.fillRect(silo.x - 6, silo.y - 4, 12, 4);
        continue;
      }

      const glowColor = silo.ammo > 0 ? '#00ffff' : '#ff0055';
      ctx.strokeStyle = glowColor;
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 10 + kick * 10;
      ctx.lineWidth = 3;
      ctx.fillStyle = '#0a2236';

      ctx.beginPath();
      ctx.arc(silo.x, silo.y, 20, Math.PI, 0);
      ctx.fill();
      ctx.stroke();

      // Ammo indicator LED dots
      ctx.shadowBlur = 0;
      const dots = silo.ammo;
      for (let i = 0; i < silo.maxAmmo; i++) {
        const angle = Math.PI + (i / (silo.maxAmmo - 1)) * Math.PI;
        const dotX = silo.x + Math.cos(angle) * 14;
        const dotY = silo.y + Math.sin(angle) * 14;

        ctx.fillStyle = i < dots ? '#00ffcc' : '#224444';
        ctx.beginPath();
        ctx.arc(dotX, dotY, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private drawBombersAndDrones(engine: SonicSiloEngine, energy: number): void {
    const ctx = this.ctx;
    ctx.save();

    // Draw Orbital Bombers
    for (const b of engine.bombers) {
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.strokeStyle = '#ff00aa';
      ctx.fillStyle = 'rgba(40, 5, 30, 0.85)';
      ctx.shadowColor = '#ff00aa';
      ctx.shadowBlur = 10 + energy * 8;
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.moveTo(-b.radius, 0);
      ctx.lineTo(-b.radius * 0.5, -b.radius * 0.6);
      ctx.lineTo(b.radius * 0.5, -b.radius * 0.6);
      ctx.lineTo(b.radius, 0);
      ctx.lineTo(b.radius * 0.4, b.radius * 0.5);
      ctx.lineTo(-b.radius * 0.4, b.radius * 0.5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    // Draw Supply Drones
    for (const d of engine.supplyDrones) {
      ctx.save();
      const color = d.type === 'repair' ? '#00ff66' : d.type === 'emp' ? '#00ffff' : '#ffcc00';
      const label = d.type === 'repair' ? '+' : d.type === 'emp' ? 'E' : '3X';
      ctx.strokeStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = 12;
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.arc(d.x, d.y, d.radius, 0, Math.PI * 2);
      ctx.stroke();

      ctx.fillStyle = color;
      ctx.font = 'bold 10px "Courier New", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, d.x, d.y);
      ctx.restore();
    }

    ctx.restore();
  }

  private drawInterceptors(engine: SonicSiloEngine): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.lineWidth = 2;

    for (const r of engine.interceptors) {
      const color = r.empCharged ? '#00ffaa' : '#00ffff';
      ctx.strokeStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = r.empCharged ? 10 : 6;

      ctx.beginPath();
      ctx.moveTo(r.startX, r.startY);
      ctx.lineTo(r.x, r.y);
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(r.x, r.y, 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      const ts = 5;
      ctx.beginPath();
      ctx.moveTo(r.targetX - ts, r.targetY - ts);
      ctx.lineTo(r.targetX + ts, r.targetY + ts);
      ctx.moveTo(r.targetX + ts, r.targetY - ts);
      ctx.lineTo(r.targetX - ts, r.targetY + ts);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawWarheads(engine: SonicSiloEngine): void {
    const ctx = this.ctx;
    ctx.save();

    for (const w of engine.warheads) {
      const isFrozen = w.stasisTimer > 0;
      const color = isFrozen ? '#00ffff' : w.color;

      ctx.strokeStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = 8;
      ctx.lineWidth = 1.5;

      ctx.beginPath();
      ctx.moveTo(w.startX, w.startY);
      ctx.lineTo(w.x, w.y);
      ctx.stroke();

      ctx.fillStyle = isFrozen ? '#00ffff' : '#ffffff';
      ctx.beginPath();
      ctx.arc(w.x, w.y, w.radius, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawShockwaves(engine: SonicSiloEngine, energy: number): void {
    const ctx = this.ctx;
    ctx.save();

    for (const sw of engine.shockwaves) {
      const alpha = Math.max(0, 1 - sw.age / sw.maxAge);
      ctx.strokeStyle = sw.color;
      ctx.shadowColor = sw.color;
      ctx.shadowBlur = 12 + energy * 10;
      ctx.lineWidth = sw.isSecondary ? 3 : 2;
      ctx.globalAlpha = alpha;

      ctx.beginPath();
      ctx.arc(sw.x, sw.y, sw.radius, 0, Math.PI * 2);
      ctx.stroke();

      if (sw.isSecondary || sw.empCharged) {
        ctx.fillStyle = sw.color;
        ctx.globalAlpha = alpha * 0.16;
        ctx.fill();
      }

      if (sw.comboChain > 1) {
        ctx.globalAlpha = alpha;
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 12px "Courier New", monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${sw.comboChain}X`, sw.x, sw.y);
      }
    }

    ctx.restore();
  }

  private drawParticles(engine: SonicSiloEngine): void {
    const ctx = this.ctx;
    ctx.save();

    for (const p of engine.particles) {
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.alpha;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private drawFloatingTexts(engine: SonicSiloEngine): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.font = 'bold 14px "Courier New", monospace';
    ctx.textAlign = 'center';

    for (const ft of engine.floatingTexts) {
      ctx.fillStyle = ft.color;
      ctx.shadowColor = ft.color;
      ctx.shadowBlur = 6;
      ctx.globalAlpha = ft.alpha;
      ctx.fillText(ft.text, ft.x, ft.y);
    }

    ctx.restore();
  }

  private drawReticle(engine: SonicSiloEngine): void {
    const ctx = this.ctx;
    const r = engine.reticle;

    ctx.save();

    // Gravity Pulse Aura Ring when holding Fire
    if (engine.gravityPulseActive) {
      ctx.strokeStyle = 'rgba(180, 80, 255, 0.45)';
      ctx.shadowColor = '#b450ff';
      ctx.shadowBlur = 12;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.arc(r.x, r.y, 150, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.strokeStyle = '#00ffcc';
    ctx.shadowColor = '#00ffcc';
    ctx.shadowBlur = 10;
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.arc(r.x, r.y, 12, 0, Math.PI * 2);
    ctx.stroke();

    const len = 8;
    ctx.beginPath();
    ctx.moveTo(r.x - 18, r.y);
    ctx.lineTo(r.x - 18 + len, r.y);
    ctx.moveTo(r.x + 18, r.y);
    ctx.lineTo(r.x + 18 - len, r.y);
    ctx.moveTo(r.x, r.y - 18);
    ctx.lineTo(r.x, r.y - 18 + len);
    ctx.moveTo(r.x, r.y + 18);
    ctx.lineTo(r.x, r.y + 18 - len);
    ctx.stroke();

    ctx.restore();
  }

  private drawOverlay(engine: SonicSiloEngine, kick: number): void {
    const ctx = this.ctx;
    ctx.save();

    ctx.fillStyle = 'rgba(5, 7, 20, 0.78)';
    ctx.fillRect(0, 0, engine.width, engine.height);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (engine.phase === 'START') {
      ctx.font = 'bold 28px "Courier New", monospace';
      ctx.fillStyle = '#00ffcc';
      ctx.shadowColor = '#00ffcc';
      ctx.shadowBlur = 12 + kick * 10;
      ctx.fillText('SONIC SILO DEFENSE', engine.width / 2, engine.height / 2 - 45);

      ctx.font = '15px "Courier New", monospace';
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 0;
      ctx.fillText('Aim: WASD / Arrows / Mouse / Touch', engine.width / 2, engine.height / 2 + 2);
      ctx.fillText('Fire Interceptor: SPACE / Z / Tap (Hold for Gravity Aura)', engine.width / 2, engine.height / 2 + 28);
      ctx.fillText('Fire on Beat Peaks for Harmonic EMP Stasis Blasts!', engine.width / 2, engine.height / 2 + 52);

      ctx.font = 'bold 18px "Courier New", monospace';
      ctx.fillStyle = '#ffff00';
      ctx.fillText('PRESS FIRE OR TAP TO START', engine.width / 2, engine.height / 2 + 95);
    } else if (engine.phase === 'GAME_OVER') {
      ctx.font = 'bold 36px "Courier New", monospace';
      ctx.fillStyle = '#ff0055';
      ctx.shadowColor = '#ff0055';
      ctx.shadowBlur = 16;
      ctx.fillText('GAME OVER', engine.width / 2, engine.height / 2 - 40);

      ctx.font = '20px "Courier New", monospace';
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 0;
      ctx.fillText(`FINAL SCORE: ${engine.score}`, engine.width / 2, engine.height / 2 + 10);
      ctx.fillText(`MAX COMBO: ${engine.maxCombo}x`, engine.width / 2, engine.height / 2 + 38);

      ctx.font = 'bold 18px "Courier New", monospace';
      ctx.fillStyle = '#00ffcc';
      ctx.fillText('PRESS FIRE OR TAP TO RESTART', engine.width / 2, engine.height / 2 + 85);
    }

    ctx.restore();
  }
}
